import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Plus,
  Pencil,
  Trash2,
  Download,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  FileText,
} from 'lucide-react';
import { INCOME_STATUS_COLORS } from './crmConstants';

const PAYMENT_METHODS = ['Abanca', 'Tarjeta Abanca', 'Wise', 'Paypal'];

const openInvoiceFile = async (url, originalName) => {
  try {
    const resp = await axios.get(url, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: resp.headers['content-type'] || 'application/octet-stream' }));
    const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
    if (!win) {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = originalName || 'factura';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
  } catch (err) {
    toast.error(err.response?.status === 404 ? 'Archivo no encontrado' : 'Error al abrir factura');
  }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const IncomeView = ({
  incomeInvoices,
  incomeStats,
  incomePagination,
  incomeSearch,
  setIncomeSearch,
  incomePaymentFilter,
  setIncomePaymentFilter,
  fetchIncomeInvoices,
  openIncomeDialog,
  handleDeleteIncome,
  setShowIncomeDownloadDialog,
  language,
  t,
}) => {
  const isEs = language === 'es';
  const totalCobrado = (incomeStats?.total_paid || 0).toFixed(2);
  const totalPendiente = (incomeStats?.total_pending || 0).toFixed(2);

  return (
    <div className="space-y-4" data-testid="income-view">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="border-emerald-200">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">{isEs ? 'Total Cobrado' : 'Total Collected'}</CardDescription>
            <CardTitle className="text-2xl text-emerald-600">€{totalCobrado}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-amber-200">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">{isEs ? 'Pendiente de Cobro' : 'Pending'}</CardDescription>
            <CardTitle className="text-2xl text-amber-600">€{totalPendiente}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">{isEs ? 'Nº Facturas' : 'Total Invoices'}</CardDescription>
            <CardTitle className="text-2xl">{incomePagination?.total ?? incomeInvoices.length}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder={isEs ? 'Buscar por nº, cliente, NIF...' : 'Search by #, client, NIF...'}
          value={incomeSearch}
          onChange={(e) => setIncomeSearch(e.target.value)}
          className="max-w-sm"
          data-testid="income-search-input"
        />
        <Select value={incomePaymentFilter || 'all'} onValueChange={(v) => setIncomePaymentFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44" data-testid="income-status-filter">
            <SelectValue placeholder={isEs ? 'Estado' : 'Status'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isEs ? 'Todos los estados' : 'All statuses'}</SelectItem>
            <SelectItem value="Cobrada">{isEs ? 'Cobrada' : 'Paid'}</SelectItem>
            <SelectItem value="Pendiente">{isEs ? 'Pendiente' : 'Pending'}</SelectItem>
            <SelectItem value="No pagada">{isEs ? 'No pagada' : 'Unpaid'}</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => fetchIncomeInvoices(1)} data-testid="income-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-1" />{isEs ? 'Refrescar' : 'Refresh'}
        </Button>
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowIncomeDownloadDialog(true)}
            data-testid="income-download-btn"
          >
            <Download className="h-4 w-4 mr-1" />{isEs ? 'Descargar' : 'Download'}
          </Button>
          <Button
            onClick={() => openIncomeDialog()}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="income-add-btn"
          >
            <Plus className="h-4 w-4 mr-1" />{isEs ? 'Nuevo Ingreso' : 'New Income'}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-md bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nº FACTURA</TableHead>
              <TableHead>F. FACTURA</TableHead>
              <TableHead>COBRADA</TableHead>
              <TableHead>FORMA PAGO</TableHead>
              <TableHead>CLIENTE</TableHead>
              <TableHead>Nº PEDIDO</TableHead>
              <TableHead className="text-right">BASE IMP.</TableHead>
              <TableHead className="text-right">IVA</TableHead>
              <TableHead className="text-right">TOTAL</TableHead>
              <TableHead>F. PAGO</TableHead>
              <TableHead className="text-right">{isEs ? 'Acciones' : 'Actions'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {incomeInvoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                  {isEs ? 'No hay ingresos registrados' : 'No income invoices'}
                </TableCell>
              </TableRow>
            ) : (
              incomeInvoices.map((inv) => {
                const files = inv.attached_files || inv.files || [];
                const firstFile = files[0];
                const lines = inv.order_lines || [];
                const orderBadges = lines.length > 0
                  ? lines.map(l => l.order_number).filter(Boolean)
                  : (inv.order_number ? [inv.order_number] : []);
                const statusColor = INCOME_STATUS_COLORS?.[inv.status] || 'bg-slate-100 text-slate-700';
                const ivaEuros = ((parseFloat(inv.base_amount) || 0) * (parseFloat(inv.iva) || 0) / 100).toFixed(2);
                const isPaid = inv.status === 'Cobrada' || inv.status === 'Paid';
                return (
                  <TableRow key={inv.id} data-testid={`income-row-${inv.invoice_number}`}>
                    <TableCell className="font-mono text-xs font-medium">{inv.invoice_number}</TableCell>
                    <TableCell className="text-xs">{fmtDate(inv.invoice_date)}</TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${statusColor}`}>
                        {inv.status || 'No pagada'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{inv.payment_method || '—'}</TableCell>
                    <TableCell className="text-xs max-w-[180px] truncate" title={inv.client_name}>
                      {inv.client_name || '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {orderBadges.length === 0 ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          orderBadges.map((n) => (
                            <Badge key={n} variant="outline" className="text-[10px]">#{n}</Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">€{(parseFloat(inv.base_amount) || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-xs">€{ivaEuros}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-700">€{(parseFloat(inv.total) || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-xs">
                      {inv.payment_date ? (
                        <span className="text-emerald-700">{fmtDate(inv.payment_date)}</span>
                      ) : isPaid ? (
                        <span className="text-amber-600 italic" title={isEs ? 'Falta fecha de pago' : 'Missing payment date'}>—</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {firstFile && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            title={isEs ? 'Ver factura' : 'View invoice'}
                            onClick={() => openInvoiceFile(firstFile.url, firstFile.original_name)}
                            data-testid={`income-file-${inv.invoice_number}`}
                          >
                            <FileText className="h-4 w-4 text-blue-600" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => openIncomeDialog(inv)}
                          data-testid={`income-edit-${inv.invoice_number}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteIncome(inv.id)}
                          data-testid={`income-delete-${inv.invoice_number}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {incomePagination && incomePagination.total_pages > 1 && (
        <div className="flex items-center justify-between" data-testid="income-pagination">
          <div className="text-xs text-muted-foreground">
            {isEs ? 'Página' : 'Page'} {incomePagination.current_page} / {incomePagination.total_pages} · {incomePagination.total} {isEs ? 'facturas' : 'invoices'}
          </div>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={incomePagination.current_page <= 1}
              onClick={() => fetchIncomeInvoices(incomePagination.current_page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={incomePagination.current_page >= incomePagination.total_pages}
              onClick={() => fetchIncomeInvoices(incomePagination.current_page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomeView;
